import React, {useEffect, useRef, useState} from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import {Container, DropdownButton, DropdownButtonPlaceholder, DropdownContainer, IconContainer, Input} from './dropdown.styled';
import {CaretIcon} from '../../icons/caret-icon';
import {CrossIconOld} from '../../icons/cross-icon';
import {Group} from './group';
import {TextUtils} from '../../utils/text_utils';
import {CustomScrollDiv} from '../custom-scroll-div';
import {Item} from './Item';
import AutoSizer from 'react-virtualized-auto-sizer';

export const Dropdown = ({
	inputClassName,
	name,
	isNested,
	disabled,
	selectedItems,
	selectedGroupName,
	isMultiSelect,
	isClearable,
	headerLines,
	dropdownAlignment,
	width,
	maxHeight,
	usePortal,
	children,
	onSelect,
	onRemove,
	userpilot,
	emphasizeEmptyState,
	cy,
	size,
	showSelectedCount,
	customPlaceholder,
	onExpand,
	searchStringChangedCallback,
	focusOnMount,
	hideSelected,
	hideEmptyGroups,
	arrowCollapseFix,
}) => {
	const [searchString, setSearchString] = useState('');
	const [expanded, setExpanded] = useState(false);
	const [above, setAbove] = useState(false);
	const [focusIndex, setFocusIndex] = useState(-1);
	const scrollRef = useRef(null);
	const inputRef = useRef();
	const itemRefs = useRef({});
	const values = useRef({});

	/*************************************************************
	 * Used for listening outside clicks and close the dropdown
	 */
	const dropdownRef = useRef(null);
	const dropdownContainerRef = useRef(null);

	const dropdownContainerName = `dropdown-container-${name}`;

	let numberOfElements = 0;

	/**
	 * calls onSelect with the value selected, and an array of all selected values
	 * @param {string|number} value
	 * @returns {void}
	 */

	/**
	 *  helper to add all from a group
	 * @param {string[]|number[]} values
	 * @returns {void}
	 */
	const handleSelectAll = values => {
		onSelect([...values], [...values]);
	};

	/**
	 * calls onRemove with the value to be removed, and an array of all selected values after removal
	 * @param {string|number} value
	 * @returns {void}
	 */
	const handleRemove = value => {
		const filtered = selectedItems.filter(item => item !== value);
		onRemove([value], filtered);
		inputRef.current && inputRef.current.focus();
	};

	/**
	 *  helper to remove all fro dropdown
	 * @param {string[]|number[]} values
	 */
	const handleRemoveAll = values => {
		onRemove([...values], []);
	};

	/**
	 *
	 * @param {'up'|'down'} direction
	 */
	const arrowSelect = direction => {
		if (direction === 'up') {
			setFocusIndex(focusIndex - 1 < 0 ? 0 : focusIndex - 1);
		}
		if (direction === 'down') {
			setFocusIndex(focusIndex + 1 >= numberOfElements ? focusIndex : focusIndex + 1);
		}
	};

	const isTargetInCaret = target => {
		if (target && target.className.includes('caret-container')) {
			return true;
		}

		return false;
	};

	const isTargetInDropdown = (target, level) => {
		if (
			target &&
			target.className &&
			(target.className.includes(dropdownContainerName) || target.className.includes('chartjs-size-monitor'))
		) {
			return true;
		} else if (target && level > 0) {
			return isTargetInDropdown(target.parentNode, level - 1);
		} else {
			return false;
		}
	};

	const collapseDropdown = () => {
		document.removeEventListener('scroll', scrollEvent, true);
		setSearchString('');
		if (searchStringChangedCallback) searchStringChangedCallback('');
		setFocusIndex(-1);
		inputRef.current && inputRef.current.blur();
		setExpanded(false);
	};

	const openDropdown = () => {
		if (!disabled) {
			setExpanded(true);
			onExpand();
			inputRef.current && inputRef.current.focus();
			document.addEventListener('scroll', scrollEvent, true);
		}
	};

	function scrollEvent(e) {
		if (e.target) {
			if (!isTargetInDropdown(e.target, 3)) {
				collapseDropdown();
			}
		}
	}

	const handleSelect = value => {
		if (isMultiSelect) {
			onSelect([value], [...selectedItems, value]);
		} else {
			onSelect([value], [value]);
			return collapseDropdown();
		}
		inputRef.current && inputRef.current.focus();
	};

	/**
	 *
	 * @returns {void}
	 */
	const enterKeyLogic = () => {
		if (focusIndex >= 0) {
			if (selectedItems.some(item => item === values.current[focusIndex])) {
				handleRemove(values.current[focusIndex]);
			} else {
				handleSelect(values.current[focusIndex]);
			}
		}
	};

	/**
	 *
	 * @param {React.KeyboardEventHandler} keyboardEvent
	 */
	const handleKeyDown = keyboardEvent => {
		if (keyboardEvent.key === 'ArrowUp') {
			arrowSelect('up');
		}
		if (keyboardEvent.key === 'ArrowDown') {
			arrowSelect('down');
		}
		if (keyboardEvent.key === 'Escape') {
			collapseDropdown();
		}
		if (keyboardEvent.key === 'Enter') {
			enterKeyLogic();
		}
	};
	/**
	 *
	 * @param {*} event
	 */
	const handleClickOutside = event => {
		if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
			if (dropdownContainerRef.current && !dropdownContainerRef.current.contains(event.target)) {
				collapseDropdown();
			}
		}
	};

	useEffect(() => {
		if (focusOnMount) {
			openDropdown();
		}

		document.addEventListener('mousedown', handleClickOutside);
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, []);

	const handleBlurOutside = e => {
		const newTarget = e.relatedTarget || e.explicitOriginalTarget;
		if (newTarget && !isTargetInDropdown(newTarget, 9)) {
			collapseDropdown();
		}
	};

	const handleArrowClick = () => {
		if (expanded) {
			collapseDropdown();
		} else {
			openDropdown();
		}
	};

	const setFocusOnInput = () => {
		inputRef.current && inputRef.current.focus();
	};

	const handleClearClick = e => {
		e.stopPropagation();
		if (!expanded) {
			handleRemoveAll(selectedItems);
		} else {
			setSearchString('');
			searchStringChangedCallback('');
			setFocusOnInput();
		}
	};

	const scrollTo = element => {
		scrollRef && scrollRef.current && scrollRef.current.setScrollTop(element.offsetTop - maxHeight + element.offsetHeight);
	};
	/**
	 * Iterating through children, and putting selected items in selectd group, and removing
	 * selected items from other groups
	 */

	const shouldFocus = index => {
		if (index === focusIndex) {
			itemRefs.current[index] && scrollTo(itemRefs.current[index]);
			return true;
		}
	};

	const selected = [];
	const selectedValues = new Set();
	const groups = [];
	const items = [];
	let nextIndex = 0;

	if (isNested) {
		React.Children.forEach(children, group => {
			if (!group) {
				return;
			}
			const groupProps = group.props;
			if (groupProps.children) {
				React.Children.forEach(groupProps.children, element => {
					if (selectedItems.includes(element.props.value)) {
						if (!selectedValues.has(element.props.value)) {
							selected.push(React.cloneElement(element));
							selectedValues.add(element.props.value);
							if (isMultiSelect) {
								values.current[nextIndex++] = element.props.value;
							}
						}
					}
				});
			}
		});

		React.Children.forEach(children, group => {
			if (!group) {
				return;
			}
			const groupProps = group.props;
			const isSearching = searchString.trim() !== '';
			if (groupProps.children) {
				const startIndex = nextIndex;
				const children = React.Children.toArray(groupProps.children).filter(element => {
					if (isMultiSelect && selectedItems.includes(element.props.value)) {
						return false;
					}
					const included =
						!isSearching || TextUtils.normalizedIncludes(element.props.searchString, searchString.trim());
					if (included) {
						values.current[nextIndex++] = element.props.value;
						return true;
					}

					return false;
				});
				if (!children.length && hideEmptyGroups) {
					return;
				}
				groups.push(
					React.cloneElement(group, {
						onClick: setFocusOnInput,
						onNavigation: handleKeyDown,
						onElementClick: handleSelect,
						onSelectAllClick: e => {
							e.stopPropagation();
							handleSelectAll(
								React.Children.toArray(groupProps.children).reduce((result, element) => {
									if (!selectedItems.includes(element.props.value)) {
										return [...result, element.props.value];
									} else {
										return result;
									}
								}, [])
							);
						},
						onFocus: scrollTo,
						actionType: /*isMultiSelect ? 'select' :*/ null,
						// key: groupProps.key,
						focusIndex,
						startIndex,
						isSearching,
						cy: groupProps.cy || `group-${groupProps.name}`,
						children,
					})
				);
			}
		});
	} else {
		React.Children.forEach(children, element => {
			if (selectedItems.includes(element.props.value)) {
				selected.push(React.cloneElement(element));
			}
		});
		React.Children.forEach(children, child => {
			const included =
				(searchString.trim() === '' || TextUtils.normalizedIncludes(child.props.searchString, searchString.trim())) &&
				!(hideSelected && selectedItems.includes(child.props.value));
			if (included) {
				let index = nextIndex++;
				values.current[index] = child.props.value;
				items.push(
					<Item
						cy={child.props.cy}
						onSetInnerRef={ref => (itemRefs.current[index] = ref)}
						onKeyDown={handleKeyDown}
						showFocus={shouldFocus(index)}
						onClick={child.props.disabled ? () => null : handleSelect}
						key={child.props.value}
						value={child.props.value}
					>
						{child}
					</Item>
				);
			}
		});
	}
	numberOfElements = nextIndex;

	const getDropdownContent = (style, positionAbove, autoWidth) => {
		if (isNested) {
			return (
				<DropdownContainer
					positionAbove={positionAbove}
					className={dropdownContainerName}
					data-cy={'dropdown-container'}
					ref={dropdownContainerRef}
					style={style}
					width={width || autoWidth}
					maxHeight={maxHeight}
					dropdownAlignment={dropdownAlignment}
				>
					<CustomScrollDiv
						ref={scrollRef}
						scrollParent={'nested-dropdown'}
						autoHeight={true}
						autoHeightMin={1}
						autoHeightMax={maxHeight}
					>
						{headerLines}
						{isMultiSelect && !!selectedGroupName && (
							<Group
								onFocus={scrollTo}
								onRemoveAllClick={e => {
									e.stopPropagation();
									handleRemoveAll(React.Children.map(selected, child => child.props.value));
								}}
								key={'selected-1'}
								cy={'group-selected'}
								actionType={/*isMultiSelect && false ? 'clear' :*/ null} // still working on bulk actions
								onClick={setFocusOnInput}
								onNavigation={handleKeyDown}
								startIndex={0}
								focusIndex={focusIndex}
								name={selectedGroupName}
								onElementClick={setFocusOnInput}
								onRemoveClick={handleRemove}
							>
								{selected}
							</Group>
						)}
						{groups}
					</CustomScrollDiv>
				</DropdownContainer>
			);
		} else {
			return (
				<DropdownContainer
					positionAbove={positionAbove}
					className={dropdownContainerName}
					ref={dropdownContainerRef}
					style={style}
					width={autoWidth || width}
					maxHeight={maxHeight}
					dropdownAlignment={dropdownAlignment}
				>
					<CustomScrollDiv
						ref={scrollRef}
						scrollParent={'nested-dropdown'}
						//handleScroll={this.props.handleScroll}
						autoHeight={true}
						autoHeightMin={1}
						autoHeightMax={maxHeight}
					>
						{items}
					</CustomScrollDiv>
				</DropdownContainer>
			);
		}
	};

	const handleSetSearchString = e => {
		setSearchString(e.target.value);
		setFocusIndex(-1);

		if (searchStringChangedCallback) {
			searchStringChangedCallback(e.target.value);
		}
	};
	const getNameAndCount = (name, count) => `${name}${showSelectedCount && count > 0 ? ` (${count})` : ''}`;
	const handleDropdownContainerClick = () => {
		if (!expanded) {
			openDropdown();
		}
	};

	const showDropdownClearIcon = () => (!expanded && isClearable && selected.length > 0 && !disabled) || searchString !== '';
	const getDropdown = width => {
		if (!expanded) return null;

		let positionAbove = false;
		const offset = dropdownRef.current.getBoundingClientRect();
		const maxDropdownBottomPosition = offset.bottom + maxHeight;
		const haveSpaceAbove = offset.top - maxHeight >= 0;
		if (maxDropdownBottomPosition > window.innerHeight && haveSpaceAbove) {
			positionAbove = true;
			if (above !== positionAbove) {
				setAbove(positionAbove);
			}
		}

		let style = {};
		if (usePortal) {
			if (offset) {
				style = {
					position: 'absolute',
					zIndex: 999,
					//top: offset.top + offset.height - 1,
					left:
						dropdownAlignment === 'left' || dropdownAlignment === 'none'
							? offset.left
							: dropdownAlignment === 'center'
							? offset.left + (offset.right - offset.left) / 2
							: undefined,
					right: dropdownAlignment === 'right' ? window.innerWidth - offset.right : undefined,
				};
				if (positionAbove) {
					style.bottom = window.innerHeight - offset.top - 1;
				} else {
					style.top = offset.top + offset.height - 1;
				}
			}
			return ReactDOM.createPortal(
				getDropdownContent(style, positionAbove, width),
				document.querySelector('#root-portal-container')
			);
		} else {
			if (positionAbove) {
				style.bottom = size === 'large' ? '38px' : '28px';
			}
		}

		return getDropdownContent(style, positionAbove, width);
	};

	const getCollapsedPlaceholder = () => {
		if (isMultiSelect) {
			return customPlaceholder ? <DropdownButtonPlaceholder>{customPlaceholder}</DropdownButtonPlaceholder> : null;
		} else if (!expanded) {
			return selected.length > 0 ? <DropdownButtonPlaceholder>{selected[0]}</DropdownButtonPlaceholder> : null;
		}
	};

	const handleFocus = e => {
		if (arrowCollapseFix && (isTargetInCaret(e.target) || isTargetInCaret(e.relatedTarget))) {
			return;
		}
		if (!expanded) {
			openDropdown();
		}
	};

	return (
		<>
			<Container
				data-cy={cy}
				disabled={disabled}
				expanded={expanded}
				above={above}
				className={dropdownContainerName}
				ref={dropdownRef}
				onClick={handleDropdownContainerClick}
				onFocus={handleFocus}
				onBlur={handleBlurOutside}
				data-userpilot={userpilot}
				size={size}
			>
				<DropdownButton>
					<Input
						className={inputClassName}
						disabled={disabled}
						emphasizePlaceholder={emphasizeEmptyState}
						onKeyDown={handleKeyDown}
						ref={inputRef}
						placeholder={getNameAndCount(name, selectedItems.length)}
						expanded={expanded}
						value={expanded ? searchString : ''}
						onChange={handleSetSearchString}
					/>
					{!expanded && getCollapsedPlaceholder()}
					{showDropdownClearIcon() && (
						<IconContainer onClick={handleClearClick} data-cy={'clear-icon'}>
							<CrossIconOld color={'#535353'} />
						</IconContainer>
					)}
					<IconContainer
						className={'caret-container'}
						onClick={handleArrowClick}
						tabIndex={arrowCollapseFix ? 1 : null}
					>
						<CaretIcon
							color={!disabled ? '#535353' : '#a1a1a1'}
							direction={expanded ? CaretIcon.DIRECTION.UP : CaretIcon.DIRECTION.DOWN}
							tabIndex="-1"
						/>
					</IconContainer>
				</DropdownButton>
				{usePortal ? <AutoSizer>{({width}) => getDropdown(width)}</AutoSizer> : getDropdown()}
			</Container>
		</>
	);
};

Dropdown.propTypes = {
	inputClassName: PropTypes.string,
	onExpand: PropTypes.func,
	isNested: PropTypes.bool,
	/**
	 * disables selected new options in the dropdown
	 */
	disabled: PropTypes.bool,
	/**
	 * enable dark text color on placeholder when no option is selected
	 */
	emphasizeEmptyState: PropTypes.bool,
	/**
	 * enabling option to clear dropdown without opening it
	 */
	isClearable: PropTypes.bool,
	/**
	 * Extra lines that can be placed in the top of the dropdown,
	 * and is not part of the selection logic.
	 */
	headerLines: PropTypes.any,
	/**
	 * Show number of selected count in parenteses
	 */
	showSelectedCount: PropTypes.number,
	/**
	 * Children should be of type Dropdown.Group
	 * These groups should contain components which have atleast two props (searchString and value)
	 * The props are used for selectiong and searching
	 */
	children: PropTypes.any,
	/**
	 * Custom visual to show when dropdown is collapsed
	 */
	customPlaceholder: PropTypes.any,
	/**
	 * name displayed on the dropdown when collapsed
	 */
	name: PropTypes.string.isRequired,

	/**
	 * Array of values of the selected items, can be number or string
	 */
	selectedItems: PropTypes.array,

	/**
	 * If nested, name to display on the group which contains selected items. If set to null, will hide selections.
	 */
	selectedGroupName: PropTypes.string,
	/**
	 * can you select multiple valueses - multiselect is default and single is not developed yet
	 */
	isMultiSelect: PropTypes.bool,
	/**
	 * If not nested, hides the already selected items
	 */
	hideSelected: PropTypes.bool,
	/**
	 * If nested, hides empty groups
	 */
	hideEmptyGroups: PropTypes.bool,

	/**
	 * Pixel width of the dropdown
	 */
	width: PropTypes.number,

	/**
	 * Pixel max height of the dropdown
	 */
	maxHeight: PropTypes.number,

	/**
	 * a function which takes a value and an array of values, will be called when an item is removed, with the value to be removed and all selected values after removal
	 */
	onRemove: PropTypes.func,

	/**
	 * a function which takes a value and an array of values, will be called when an item is selected, with the selected value and all selected values
	 */
	onSelect: PropTypes.func,

	/**
	 * Alignment of the dropdown to the dropdown input
	 */
	dropdownAlignment: PropTypes.oneOf(['left', 'center', 'right', 'none']),

	/**
	 * show dropdown in a portal ( to be implemented)
	 */
	usePortal: PropTypes.bool,
	/**
	 * ID needed for onboarding tool
	 */
	userpilot: PropTypes.string,
	cy: PropTypes.string,
	size: PropTypes.string,
	/**
	 * Function to be called when the search string is changed
	 */
	searchStringChangedCallback: PropTypes.func,
	/**
	 * Force dropdown to take focus as soon as it's mounted
	 */
	focusOnMount: PropTypes.bool,
	/**
	 * Experimental approach to prevent dropdown from expanding again when clicking arrow to collapse dropdown
	 */
	arrowCollapseFix: PropTypes.bool,
};

Dropdown.defaultProps = {
	selectedGroupName: 'Selected',
	selectedItems: [],
	maxHeight: 300,
	dropdownAlignment: 'none',
	onSelect: () => false,
	onRemove: () => false,
	onExpand: () => false,
	searchStringChangedCallback: () => false,
};
